import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import FMPPreviewModal from './FMPPreviewModal';

const API_BASE_URL = `${window.location.protocol}//${window.location.hostname}/api`;

function api(path, options = {}) {
  const token = localStorage.getItem('token');
  return fetch(`${API_BASE_URL}${path}`, {
    ...options,
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}`, ...options.headers }
  }).then(r => r.json());
}

const EQUIPMENT_TYPES = [
  { key: 'fiber_housing_2u', label: 'Fiber Housing 2U', color: '#3182ce', height: 2 },
  { key: 'fiber_housing_4u', label: 'Fiber Housing 4U', color: '#3182ce', height: 4 },
  { key: 'router',           label: 'Router',           color: '#805ad5', height: 2 },
  { key: 'switch',           label: 'Switch',           color: '#38a169', height: 1 },
  { key: 'ciena',            label: 'Ciena Device',     color: '#d69e2e', height: 1 },
  { key: 'sel',              label: 'SEL',              color: '#e53e3e', height: 4 },
  { key: 'patch_panel',      label: 'Patch Panel',      color: '#718096', height: 1 },
  { key: 'cable_mgmt',       label: 'Cable Mgmt',       color: '#a0aec0', height: 1 },
  { key: 'amplifier',        label: 'Amplifier',        color: '#e53e3e', height: 1 },
  { key: 'empty',            label: 'Blanking Panel',   color: '#e2e8f0', height: 1 },
];

const FEED_SIDE_LABELS = { left: 'Left', right: 'Right', other: 'Other' };
const FEED_SIDE_COLORS = { left: '#3182ce', right: '#e53e3e', other: '#718096' };

function InlineField({ label, value, onChange, type = 'text', options }) {
  if (options) return (
    <div style={{ marginBottom: 10 }}>
      <label style={{ fontSize: 12, color: '#718096', display: 'block', marginBottom: 3 }}>{label}</label>
      <select value={value || ''} onChange={e => onChange(e.target.value)}
        style={{ width: '100%', padding: '7px 8px', border: '1px solid #e2e8f0', borderRadius: 6, fontSize: 13, backgroundColor: 'white' }}>
        {options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
      </select>
    </div>
  );
  return (
    <div style={{ marginBottom: 10 }}>
      <label style={{ fontSize: 12, color: '#718096', display: 'block', marginBottom: 3 }}>{label}</label>
      <input type={type} value={value || ''} onChange={e => onChange(e.target.value)}
        style={{ width: '100%', padding: '7px 8px', border: '1px solid #e2e8f0', borderRadius: 6, fontSize: 13, boxSizing: 'border-box' }} />
    </div>
  );
}

export default function RackView() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [rack, setRack] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dragType, setDragType] = useState(null);
  const [hoveredUnit, setHoveredUnit] = useState(null);
  const [addingAt, setAddingAt] = useState(null);
  const [newLabel, setNewLabel] = useState('');
  const [selectedEquipment, setSelectedEquipment] = useState(null);
  const [editingEquipment, setEditingEquipment] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [editingFeed, setEditingFeed] = useState(null);
  const [feedsOpen, setFeedsOpen] = useState(true);
  const [fuseBlocks, setFuseBlocks] = useState([]);
  const [showFmpForm, setShowFmpForm] = useState(false);
  const [fmpForm, setFmpForm] = useState({ change_summary: '', fmp_type: 'rack_view', no_change: false });
  const [fmpPreview, setFmpPreview] = useState(null);
  const [submittingFmp, setSubmittingFmp] = useState(false);
  const isMobile = typeof window !== 'undefined' && window.innerWidth <= 700;

  const BLANK_FEED = { feed_name: '', cable_number: '', cabinet_name: '', breaker_number: '', feed_side: 'left', feed_type: 'dc_substation', voltage_vdc: '125', fuse_count: '', notes: '' };

  useEffect(() => {
    api(`/racks/${id}`).then(data => { setRack(data); setLoading(false); });
    api(`/racks/${id}/power-feeds`).then(data => { if (data.fuse_blocks) setFuseBlocks(data.fuse_blocks); });
  }, [id]);

  const getEquipmentAt = (unit) => {
    if (!rack) return null;
    return rack.equipment?.find(e => unit >= e.rack_unit_start && unit < e.rack_unit_start + e.rack_unit_height);
  };

  const isUnitFree = (unit) => !getEquipmentAt(unit);

  const canPlace = (startUnit, height) => {
    for (let i = 0; i < height; i++) {
      if (startUnit + i > rack.total_units) return false;
      if (!isUnitFree(startUnit + i)) return false;
    }
    return true;
  };

  const handleDrop = (unit) => {
    if (!dragType) return;
    const eqType = EQUIPMENT_TYPES.find(t => t.key === dragType);
    if (!canPlace(unit, eqType.height)) return;
    setAddingAt({ unit, type: dragType, height: eqType.height });
    setNewLabel('');
    setDragType(null);
  };

  const reloadRack = () => {
    api(`/racks/${id}`).then(data => setRack(data));
    api(`/racks/${id}/power-feeds`).then(data => {
      if (data.fuse_blocks) setFuseBlocks(data.fuse_blocks);
    });
  };

  const saveEquipmentEdit = async () => {
    if (!editingEquipment) return;
    const result = await api(`/racks/item/${editingEquipment.id}`, {
      method: 'PATCH',
      body: JSON.stringify({
        label:            editingEquipment.label,
        manufacturer:     editingEquipment.manufacturer,
        model:            editingEquipment.model,
        serial_number:    editingEquipment.serial_number,
        notes:            editingEquipment.notes,
        power_supply_a:   editingEquipment.power_supply_a,
        power_supply_b:   editingEquipment.power_supply_b,
        breaker_panel:    editingEquipment.breaker_panel,
        breaker_number:   editingEquipment.breaker_number,
        breaker_amperage: editingEquipment.breaker_amperage,
        power_draw_watts: editingEquipment.power_draw_watts,
      })
    });

    // Sync fuse block assignments — release old, assign new
    // First release all fuse blocks currently assigned to this equipment
    const currentlyAssigned = fuseBlocks.filter(f => f.assigned_to_equipment_id === editingEquipment.id);
    for (const fuse of currentlyAssigned) {
      await api(`/racks/fuse-blocks/${fuse.id}/assign`, { method: 'PATCH', body: JSON.stringify({ equipment_id: null }) });
    }
    // Then assign the newly selected fuse blocks by label
    for (const [field, side] of [['power_supply_a','left'],['power_supply_b','right']]) {
      const label = editingEquipment[field];
      if (!label) continue;
      const fuse = fuseBlocks.find(f => f.label === label && f.side === side && !f.is_reserved);
      if (fuse) {
        await api(`/racks/fuse-blocks/${fuse.id}/assign`, { method: 'PATCH', body: JSON.stringify({ equipment_id: editingEquipment.id }) });
      }
    }

    setRack(prev => ({ ...prev, equipment: prev.equipment.map(e => e.id === result.id ? { ...e, ...result } : e) }));
    setSelectedEquipment(prev => prev?.id === result.id ? { ...prev, ...result } : prev);
    setEditingEquipment(null);
    reloadRack();
  };

  const saveFeed = async () => {
    if (!editingFeed) return;
    if (editingFeed.id) {
      await api(`/racks/power-feeds/${editingFeed.id}`, { method: 'PATCH', body: JSON.stringify(editingFeed) });
    } else {
      await api(`/racks/${id}/power-feeds`, { method: 'POST', body: JSON.stringify(editingFeed) });
    }
    setEditingFeed(null);
    reloadRack();
  };

  const deleteFeed = async (feed) => {
    if (!window.confirm(`Delete feed "${feed.feed_name}"?`)) return;
    await api(`/racks/power-feeds/${feed.id}`, { method: 'DELETE' });
    reloadRack();
  };

  const assignFuse = async (fuseId, equipmentId, daisyFromId, notes) => {
    await api(`/racks/fuse-blocks/${fuseId}/assign`, {
      method: 'PATCH',
      body: JSON.stringify({ equipment_id: equipmentId || null, daisy_chained_from: daisyFromId || null, notes: notes || null }),
    });
    reloadRack();
  };

  const previewStandaloneFmp = () => {
    if (!fmpForm.no_change && !fmpForm.change_summary.trim()) return;
    // Build preview from current rack state in memory
    const preview = {
      fmp_type: fmpForm.fmp_type,
      no_change: fmpForm.no_change,
      change_summary: fmpForm.change_summary,
      location_name: rack.location_name || '—',
      rack_label: rack.label,
      generated_by_name: 'You',
      generated_at: new Date().toISOString(),
      rack_snapshot: {
        rack,
        equipment: rack.equipment || [],
        captured_at: new Date().toISOString(),
        previous_snapshot: null, // loaded from last published FMP on backend
      },
    };
    setFmpPreview(preview);
  };

  const submitStandaloneFmp = async () => {
    setSubmittingFmp(true);
    await api(`/racks/${id}/fmp`, { method: 'POST', body: JSON.stringify(fmpForm) });
    setSubmittingFmp(false);
    setFmpPreview(null);
    setShowFmpForm(false);
    setFmpForm({ change_summary: '', fmp_type: 'rack_view', no_change: false });
  };

  const deleteEquipment = async (eq) => {
    if (!window.confirm(`Delete "${eq.label}"?`)) return;
    await api(`/racks/item/${eq.id}`, { method: 'DELETE' });
    setRack(prev => ({ ...prev, equipment: prev.equipment.filter(e => e.id !== eq.id) }));
    setSelectedEquipment(null);
  };

  const confirmAdd = async () => {
    if (!newLabel.trim() || !addingAt) return;
    const eqType = EQUIPMENT_TYPES.find(t => t.key === addingAt.type);
    const token = localStorage.getItem('token');

    // Create rack equipment
    const eqResult = await api(`/racks/${id}/equipment`, {
      method: 'POST',
      body: JSON.stringify({
        equipment_type: addingAt.type,
        label: newLabel,
        rack_unit_start: addingAt.unit,
        rack_unit_height: eqType.height,
      })
    });

    // If it's a fiber housing, auto-create the housing record
    if (addingAt.type === 'fiber_housing_2u' || addingAt.type === 'fiber_housing_4u') {
      const chassisType = addingAt.type === 'fiber_housing_4u' ? '12-slot' : '4-slot';
      await api(`/housings`, {
        method: 'POST',
        body: JSON.stringify({
          rack_equipment_id: eqResult.id,
          location_id: rack.location_id,
          chassis_type: chassisType,
          label: newLabel,
        })
      });
    }

    // Refresh rack data
    const updated = await api(`/racks/${id}`);
    setRack(updated);
    setAddingAt(null);
    setNewLabel('');
  };

  if (loading) return <div style={{ padding: 32, fontFamily: 'sans-serif' }}>Loading...</div>;
  if (!rack) return <div style={{ padding: 32, fontFamily: 'sans-serif' }}>Rack not found.</div>;

  const units = Array.from({ length: rack.total_units }, (_, i) => i + 1);
  const renderedUnits = new Set();

  return (
    <div style={{ display: 'flex', height: '100vh', fontFamily: 'sans-serif', backgroundColor: '#f7fafc', position: 'relative' }}>

      {/* Sidebar */}
      <div style={{
        width: isMobile ? '100%' : 220,
        maxWidth: isMobile ? 320 : 'none',
        backgroundColor: 'white',
        borderRight: '1px solid #e2e8f0',
        display: 'flex', flexDirection: 'column',
        position: isMobile ? 'fixed' : 'relative',
        top: isMobile ? 0 : 'auto',
        left: isMobile ? (sidebarOpen ? 0 : '-100%') : 'auto',
        height: isMobile ? '100vh' : 'auto',
        zIndex: isMobile ? 2000 : 'auto',
        transition: isMobile ? 'left 0.25s ease' : 'none',
        boxShadow: isMobile && sidebarOpen ? '4px 0 16px rgba(0,0,0,0.2)' : 'none',
      }}>
        <div style={{ padding: '14px 16px', backgroundColor: '#2d3748', color: 'white', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <div style={{ fontWeight: 'bold', fontSize: 15 }}>{rack.label}</div>
            <div style={{ fontSize: 12, opacity: 0.7, marginTop: 2 }}>{rack.total_units}U Rack</div>
          </div>
          {isMobile && <button onClick={() => setSidebarOpen(false)} style={{ backgroundColor: 'transparent', border: 'none', color: 'white', fontSize: 20, cursor: 'pointer' }}>✕</button>}
        </div>
        <div style={{ padding: '12px 16px', borderBottom: '1px solid #e2e8f0' }}>
          <button onClick={() => navigate(-1)} style={{ width: '100%', padding: '7px', backgroundColor: '#edf2f7', border: '1px solid #e2e8f0', borderRadius: 6, cursor: 'pointer', fontSize: 13 }}>&larr; Back</button>
        </div>
        <div style={{ padding: '12px 16px', flex: 1, overflowY: 'auto' }}>
          <div style={{ fontSize: 12, fontWeight: 'bold', color: '#718096', marginBottom: 10, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Drag to add</div>
          {EQUIPMENT_TYPES.map(eq => (
            <div key={eq.key}
              draggable
              onDragStart={() => { setDragType(eq.key); if (isMobile) setSidebarOpen(false); }}
              onDragEnd={() => setDragType(null)}
              style={{ padding: '8px 10px', marginBottom: 6, backgroundColor: eq.color + '18', border: `1px solid ${eq.color}44`, borderRadius: 6, cursor: 'grab', fontSize: 13, display: 'flex', alignItems: 'center', gap: 8 }}>
              <div style={{ width: 10, height: 10, borderRadius: 2, backgroundColor: eq.color, flexShrink: 0 }} />
              <div>
                <div style={{ fontWeight: 500 }}>{eq.label}</div>
                <div style={{ fontSize: 11, color: '#a0aec0' }}>{eq.height}U</div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Mobile sidebar backdrop */}
      {isMobile && sidebarOpen && (
        <div onClick={() => setSidebarOpen(false)} style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.4)', zIndex: 1999 }} />
      )}

      {/* Rack grid + right power panel */}
      <div style={{ flex: 1, display: 'flex', overflowY: 'auto' }}>
      <div style={{ flex: 1, padding: 24 }}>
        <div style={{ maxWidth: 600, margin: '0 auto' }}>
          <div style={{ backgroundColor: '#1a202c', borderRadius: '8px 8px 0 0', padding: '10px 16px', color: 'white', fontWeight: 'bold', fontSize: 14, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <span>{rack.label} &mdash; {rack.total_units}U</span>
            <div style={{ display: 'flex', gap: 8 }}>
              {isMobile && <button onClick={() => setSidebarOpen(true)} style={{ backgroundColor: 'rgba(255,255,255,0.15)', border: 'none', color: 'white', padding: '4px 10px', borderRadius: 5, cursor: 'pointer', fontSize: 12 }}>+ Add</button>}
              <button onClick={() => setShowFmpForm(o => !o)} style={{ backgroundColor: '#c2410c', border: 'none', color: 'white', padding: '4px 12px', borderRadius: 5, cursor: 'pointer', fontSize: 12, fontWeight: 'bold' }}>
                {showFmpForm ? '✕ Close' : '📄 Generate FMP'}
              </button>
            </div>
          </div>
          {showFmpForm && (
            <div style={{ backgroundColor: '#1e293b', padding: 14, borderBottom: '2px solid #c2410c' }}>
              <div style={{ display: 'flex', gap: 10, marginBottom: 10 }}>
                <select value={fmpForm.fmp_type} onChange={e => setFmpForm(p => ({ ...p, fmp_type: e.target.value }))}
                  style={{ padding: '7px 10px', backgroundColor: '#0f172a', border: '1px solid #334155', borderRadius: 6, color: 'white', fontSize: 13 }}>
                  {[['rack_view','Rack View'],['cassette','Cassette Layout'],['routing','Routing Diagram'],['splicing','Splicing Diagram']].map(([v,l]) => <option key={v} value={v}>{l}</option>)}
                </select>
                <label style={{ display: 'flex', alignItems: 'center', gap: 6, color: '#94a3b8', fontSize: 13 }}>
                  <input type="checkbox" checked={fmpForm.no_change} onChange={e => setFmpForm(p => ({ ...p, no_change: e.target.checked }))} />
                  No change — review only
                </label>
              </div>
              {!fmpForm.no_change && (
                <textarea value={fmpForm.change_summary} onChange={e => setFmpForm(p => ({ ...p, change_summary: e.target.value }))} rows={2}
                  placeholder="Describe what changed — equipment added, removed, moved, etc."
                  style={{ width: '100%', padding: '8px 10px', backgroundColor: '#0f172a', border: `1px solid ${fmpForm.change_summary ? '#334155' : '#7f1d1d'}`, borderRadius: 6, color: 'white', fontSize: 13, resize: 'vertical', boxSizing: 'border-box', marginBottom: 8 }} />
              )}
              <button onClick={previewStandaloneFmp} disabled={!fmpForm.no_change && !fmpForm.change_summary.trim()}
                style={{ width: '100%', padding: '8px', backgroundColor: (!fmpForm.no_change && !fmpForm.change_summary.trim()) ? '#374151' : '#c2410c', color: (!fmpForm.no_change && !fmpForm.change_summary.trim()) ? '#6b7280' : 'white', border: 'none', borderRadius: 6, cursor: (!fmpForm.no_change && !fmpForm.change_summary.trim()) ? 'not-allowed' : 'pointer', fontSize: 13, fontWeight: 'bold' }}>
                👁 Preview FMP
              </button>
            </div>
          )}
          <div style={{ backgroundColor: '#2d3748', padding: '4px 0', borderLeft: '6px solid #4a5568', borderRight: '6px solid #4a5568' }}>
            {units.map(unit => {
              if (renderedUnits.has(unit)) return null;
              const eq = getEquipmentAt(unit);
              if (eq) {
                for (let i = 0; i < eq.rack_unit_height; i++) renderedUnits.add(eq.rack_unit_start + i);
                const eqType = EQUIPMENT_TYPES.find(t => t.key === eq.equipment_type);
                const color = eqType?.color || '#718096';
                const unitHeight = eq.rack_unit_height * 30;
                return (
                  <div key={unit} style={{ display: 'flex', alignItems: 'stretch', borderBottom: '1px solid #4a5568' }}>
                    <div style={{ width: 32, minHeight: unitHeight, backgroundColor: '#374151', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, color: '#9ca3af', flexShrink: 0 }}>{unit}</div>
                    <div style={{ flex: 1, minHeight: unitHeight, backgroundColor: selectedEquipment?.id === eq.id ? '#2a4a6b' : color, display: 'flex', alignItems: 'center', padding: '0 12px', gap: 8, cursor: 'pointer', border: selectedEquipment?.id === eq.id ? '2px solid #63b3ed' : '2px solid transparent' }}
                      onClick={() => setSelectedEquipment(selectedEquipment?.id === eq.id ? null : eq)}>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: 'bold', fontSize: 13, color: 'white' }}>{eq.label}</div>
                        <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.7)' }}>{eqType?.label || eq.equipment_type} · {eq.rack_unit_height}U{eq.manufacturer ? ` · ${eq.manufacturer}` : ''}</div>
                      </div>
                      {eq.housing_id && <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.8)', backgroundColor: 'rgba(0,0,0,0.2)', padding: '2px 8px', borderRadius: 4 }}>Fiber Housing</div>}
                    </div>
                  </div>
                );
              }
              renderedUnits.add(unit);
              const isHovered = hoveredUnit === unit && dragType;
              const eqType = dragType ? EQUIPMENT_TYPES.find(t => t.key === dragType) : null;
              const canDrop = dragType && canPlace(unit, eqType?.height || 1);
              return (
                <div key={unit}
                  onDragOver={e => { e.preventDefault(); setHoveredUnit(unit); }}
                  onDragLeave={() => setHoveredUnit(null)}
                  onDrop={() => { handleDrop(unit); setHoveredUnit(null); }}
                  style={{ display: 'flex', alignItems: 'stretch', borderBottom: '1px solid #4a5568', backgroundColor: isHovered ? (canDrop ? '#1a4731' : '#3d1515') : 'transparent' }}>
                  <div style={{ width: 32, height: 30, backgroundColor: '#374151', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, color: '#9ca3af', flexShrink: 0 }}>{unit}</div>
                  <div style={{ flex: 1, height: 30, display: 'flex', alignItems: 'center', padding: '0 12px' }}>
                    <div style={{ fontSize: 11, color: isHovered && canDrop ? '#68d391' : '#4a5568' }}>{isHovered && canDrop ? `Drop ${eqType?.label} here` : ''}</div>
                  </div>
                </div>
              );
            })}
          </div>
          <div style={{ backgroundColor: '#1a202c', borderRadius: '0 0 8px 8px', padding: '6px 16px', color: '#718096', fontSize: 12 }}>
            {rack.equipment?.length || 0} items installed
          </div>
        </div>

      </div>{/* end center column */}

      {/* ── DC Power Feeds — right panel ── */}
      <div style={{ width: feedsOpen ? 300 : 42, flexShrink: 0, borderLeft: '1px solid #e2e8f0', backgroundColor: 'white', display: 'flex', flexDirection: 'column', transition: 'width 0.25s ease', overflow: 'hidden' }}>
        {/* Header toggle */}
        <div onClick={() => setFeedsOpen(o => !o)} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 10px', backgroundColor: '#2d3748', color: 'white', cursor: 'pointer', flexShrink: 0, whiteSpace: 'nowrap' }}>
          {feedsOpen ? (
            <>
              <span style={{ fontWeight: 'bold', fontSize: 13 }}>⚡ DC Power Feeds</span>
              <span style={{ fontSize: 11, color: '#a0aec0' }}>◀</span>
            </>
          ) : (
            <span style={{ fontWeight: 'bold', fontSize: 13, writingMode: 'vertical-rl', transform: 'rotate(180deg)', margin: '0 auto' }}>⚡ DC Power</span>
          )}
        </div>

        {feedsOpen && (
          <div style={{ flex: 1, overflowY: 'auto', padding: 12 }}>
            {rack.power_feeds?.length === 0 && (
              <div style={{ color: '#a0aec0', fontSize: 13, textAlign: 'center', padding: '12px 0' }}>No power feeds documented yet.</div>
            )}
              {rack.power_feeds?.map(feed => (
                <div key={feed.id} style={{ display: 'flex', alignItems: 'flex-start', gap: 12, padding: '10px 12px', marginBottom: 8, backgroundColor: '#f7fafc', borderRadius: 7, border: '1px solid #e2e8f0' }}>
                  <div style={{ width: 10, height: 10, borderRadius: '50%', backgroundColor: FEED_SIDE_COLORS[feed.feed_side] || '#718096', marginTop: 4, flexShrink: 0 }} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                      <div style={{ fontWeight: 'bold', fontSize: 13 }}>{feed.feed_name}</div>
                      <div style={{ display: 'flex', gap: 6 }}>
                        <button onClick={() => setEditingFeed({ ...feed, voltage_vdc: String(feed.voltage_vdc || '125') })} style={{ padding: '3px 8px', fontSize: 11, backgroundColor: '#ebf8ff', border: '1px solid #bee3f8', borderRadius: 4, cursor: 'pointer', color: '#2b6cb0' }}>Edit</button>
                        <button onClick={() => deleteFeed(feed)} style={{ padding: '3px 8px', fontSize: 11, backgroundColor: '#fff5f5', border: '1px solid #feb2b2', borderRadius: 4, cursor: 'pointer', color: '#c53030' }}>Delete</button>
                      </div>
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 6, marginTop: 6 }}>
                      {[
                        ['Cable #', feed.cable_number],
                        ['Cabinet', feed.cabinet_name],
                        ['Breaker #', feed.breaker_number],
                        ['Side', FEED_SIDE_LABELS[feed.feed_side]],
                        ['Voltage', feed.voltage_vdc ? `${feed.voltage_vdc}VDC` : '—'],
                        ['Type', feed.feed_type?.replace(/_/g, ' ')],
                      ].map(([l, v]) => (
                        <div key={l}>
                          <div style={{ fontSize: 10, color: '#a0aec0', textTransform: 'uppercase' }}>{l}</div>
                          <div style={{ fontSize: 12, color: '#2d3748', fontWeight: 500 }}>{v || '—'}</div>
                        </div>
                      ))}
                    </div>
                    {feed.notes && <div style={{ fontSize: 11, color: '#718096', marginTop: 6 }}>{feed.notes}</div>}
                    {/* Fuse blocks for this feed */}
                    {(() => {
                      const feedFuses = fuseBlocks.filter(f => f.feed_id === feed.id);
                      if (!feedFuses.length) return null;
                      return (
                        <div style={{ marginTop: 8, borderTop: '1px solid #e2e8f0', paddingTop: 8 }}>
                          <div style={{ fontSize: 10, color: '#a0aec0', textTransform: 'uppercase', marginBottom: 6 }}>Fuse Blocks</div>
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                            {feedFuses.map(fuse => (
                              <div key={fuse.id} style={{
                                padding: '3px 8px', borderRadius: 4, fontSize: 11, fontWeight: 'bold',
                                backgroundColor: fuse.is_reserved ? '#f0f0f0' : fuse.assigned_to_equipment_id ? '#fed7aa' : '#c6f6d5',
                                color: fuse.is_reserved ? '#a0aec0' : fuse.assigned_to_equipment_id ? '#c05621' : '#276749',
                                border: `1px solid ${fuse.is_reserved ? '#e2e8f0' : fuse.assigned_to_equipment_id ? '#f6ad55' : '#9ae6b4'}`,
                                cursor: fuse.is_reserved ? 'default' : 'pointer',
                                title: fuse.is_reserved ? 'Incoming feed — reserved' : fuse.equipment_label || 'Available',
                              }}>
                                {fuse.label}
                                {fuse.is_reserved && ' 🔒'}
                                {!fuse.is_reserved && fuse.equipment_label && <span style={{ fontWeight: 'normal', marginLeft: 4 }}>→ {fuse.equipment_label}</span>}
                                {fuse.daisy_chained_from && <span style={{ color: '#e53e3e', marginLeft: 4 }}>⚠️</span>}
                              </div>
                            ))}
                          </div>
                          <div style={{ display: 'flex', gap: 12, marginTop: 6, fontSize: 11, color: '#718096' }}>
                            <span>🟢 {fuseBlocks.filter(f => f.feed_id === feed.id && !f.is_reserved && !f.assigned_to_equipment_id).length} available</span>
                            <span>🟠 {fuseBlocks.filter(f => f.feed_id === feed.id && !f.is_reserved && f.assigned_to_equipment_id).length} in use</span>
                            <span>🔒 1 reserved</span>
                          </div>
                        </div>
                      );
                    })()}
                  </div>
                </div>
              ))}
              <button onClick={() => setEditingFeed({ ...BLANK_FEED })}
                style={{ width: '100%', padding: '8px', backgroundColor: '#ebf8ff', border: '1px dashed #90cdf4', borderRadius: 7, cursor: 'pointer', fontSize: 12, color: '#2b6cb0', fontWeight: 'bold' }}>
                + Add Power Feed
              </button>
          </div>
        )}
      </div>{/* end right power panel */}
      </div>{/* end rack+power flex row */}

      {selectedEquipment && (
        <div style={{ backgroundColor: 'white', borderRadius: 8, border: '1px solid #e2e8f0', padding: 20, maxWidth: 600, margin: '16px auto 0' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
            <div style={{ fontWeight: 'bold', fontSize: 16 }}>{selectedEquipment.label}</div>
            <span onClick={() => setSelectedEquipment(null)} style={{ cursor: 'pointer', color: '#718096', fontSize: 18 }}>✕</span>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, fontSize: 13, marginBottom: 12 }}>
            {[
              ['Type',         selectedEquipment.equipment_type?.replace(/_/g,' ')],
              ['Position',     `U${selectedEquipment.rack_unit_start} · ${selectedEquipment.rack_unit_height}U`],
              ['Manufacturer', selectedEquipment.manufacturer],
              ['Model',        selectedEquipment.model],
              ['Serial #',     selectedEquipment.serial_number],
            ].map(([l, v]) => (
              <div key={l}><span style={{ color: '#718096' }}>{l}: </span>{v || '—'}</div>
            ))}
          </div>
          {(selectedEquipment.power_supply_a || selectedEquipment.power_supply_b) && (
            <div style={{ backgroundColor: '#fffbeb', border: '1px solid #fcd34d', borderRadius: 6, padding: '8px 12px', marginBottom: 12 }}>
              <div style={{ fontSize: 11, fontWeight: 'bold', color: '#92400e', marginBottom: 6, textTransform: 'uppercase' }}>⚡ Power Connections</div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, fontSize: 13 }}>
                <div><span style={{ color: '#92400e' }}>Supply A: </span>{selectedEquipment.power_supply_a || '—'}</div>
                <div><span style={{ color: '#92400e' }}>Supply B: </span>{selectedEquipment.power_supply_b || '—'}</div>
              </div>
            </div>
          )}
          {selectedEquipment.notes && <div style={{ fontSize: 12, color: '#718096', marginBottom: 12 }}>{selectedEquipment.notes}</div>}
          <div style={{ display: 'flex', gap: 8 }}>
            {selectedEquipment.housing_id && (
              <button onClick={() => navigate(`/housing/${selectedEquipment.housing_id}`)}
                style={{ flex: 1, padding: '9px', backgroundColor: '#3182ce', color: 'white', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 13, fontWeight: 'bold' }}>
                View Cassettes →
              </button>
            )}
            <button onClick={() => setEditingEquipment({ ...selectedEquipment })}
              style={{ flex: 1, padding: '9px', backgroundColor: '#ebf8ff', border: '1px solid #bee3f8', borderRadius: 6, cursor: 'pointer', fontSize: 13, color: '#2b6cb0' }}>
              ✏️ Edit
            </button>
            <button onClick={() => deleteEquipment(selectedEquipment)}
              style={{ flex: 1, padding: '9px', backgroundColor: '#fff5f5', border: '1px solid #feb2b2', borderRadius: 6, cursor: 'pointer', fontSize: 13, color: '#c53030' }}>
              🗑️ Delete
            </button>
          </div>
        </div>
      )}
      {/* Edit equipment modal */}
      {editingEquipment && (
        <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 3000, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: 16, overflowY: 'auto' }}>
          <div style={{ backgroundColor: 'white', borderRadius: 12, padding: 24, width: '100%', maxWidth: 420, boxShadow: '0 8px 32px rgba(0,0,0,0.3)', marginTop: 20 }}>
            <div style={{ fontWeight: 'bold', fontSize: 16, marginBottom: 16 }}>Edit Equipment</div>

            <div style={{ fontSize: 12, fontWeight: 'bold', color: '#718096', textTransform: 'uppercase', marginBottom: 8 }}>General</div>
            {[
              { key: 'label',         label: 'Label',        ph: 'e.g. Icon MUX' },
              { key: 'manufacturer',  label: 'Manufacturer', ph: 'e.g. ICON' },
              { key: 'model',         label: 'Model',        ph: 'e.g. 7330' },
              { key: 'serial_number', label: 'Serial #',     ph: 'e.g. SN123456' },
            ].map(({ key, label, ph }) => (
              <div key={key} style={{ marginBottom: 10 }}>
                <label style={{ fontSize: 12, color: '#718096', display: 'block', marginBottom: 3 }}>{label}</label>
                <input type="text" placeholder={ph} value={editingEquipment[key] || ''}
                  onChange={e => setEditingEquipment(p => ({ ...p, [key]: e.target.value }))}
                  style={{ width: '100%', padding: '7px 8px', border: '1px solid #e2e8f0', borderRadius: 6, fontSize: 13, boxSizing: 'border-box' }} />
              </div>
            ))}

            {!['fiber_housing_2u','fiber_housing_4u'].includes(editingEquipment.equipment_type) && (<>
              <div style={{ fontSize: 12, fontWeight: 'bold', color: '#718096', textTransform: 'uppercase', margin: '14px 0 8px', borderTop: '1px solid #e2e8f0', paddingTop: 12 }}>⚡ Power Connections</div>
              {/* Fuse dropdowns — available fuses from each side */}
              {(() => {
                const leftFuses  = fuseBlocks.filter(f => f.side === 'left'  && !f.is_reserved && (!f.assigned_to_equipment_id || f.assigned_to_equipment_id === editingEquipment.id));
                const rightFuses = fuseBlocks.filter(f => f.side === 'right' && !f.is_reserved && (!f.assigned_to_equipment_id || f.assigned_to_equipment_id === editingEquipment.id));
                return (
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                    {[
                      { key: 'power_supply_a', label: 'Supply A (Left feed)', fuses: leftFuses,  ph: 'Fuse Block e.g. 2L' },
                      { key: 'power_supply_b', label: 'Supply B (Right feed)', fuses: rightFuses, ph: 'Fuse Block e.g. 2R' },
                    ].map(({ key, label, fuses, ph }) => (
                      <div key={key} style={{ marginBottom: 10 }}>
                        <label style={{ fontSize: 12, color: '#718096', display: 'block', marginBottom: 3 }}>{label}</label>
                        {fuses.length > 0 ? (
                          <select value={editingEquipment[key] || ''} onChange={e => setEditingEquipment(p => ({ ...p, [key]: e.target.value }))}
                            style={{ width: '100%', padding: '7px 8px', border: '1px solid #e2e8f0', borderRadius: 6, fontSize: 13 }}>
                            <option value=''>— Not assigned —</option>
                            {fuses.map(f => <option key={f.id} value={f.label}>{f.label}{f.assigned_to_equipment_id === editingEquipment.id ? ' (current)' : ' — available'}</option>)}
                          </select>
                        ) : (
                          <input type="text" placeholder={ph} value={editingEquipment[key] || ''}
                            onChange={e => setEditingEquipment(p => ({ ...p, [key]: e.target.value }))}
                            style={{ width: '100%', padding: '7px 8px', border: '1px solid #e2e8f0', borderRadius: 6, fontSize: 13, boxSizing: 'border-box' }} />
                        )}
                      </div>
                    ))}
                  </div>
                );
              })()}
            </>)}

            <div style={{ fontSize: 12, fontWeight: 'bold', color: '#718096', textTransform: 'uppercase', margin: '14px 0 8px', borderTop: '1px solid #e2e8f0', paddingTop: 12 }}>Notes</div>
            <textarea value={editingEquipment.notes || ''} onChange={e => setEditingEquipment(p => ({ ...p, notes: e.target.value }))} rows={2}
              placeholder="Any additional notes..."
              style={{ width: '100%', padding: '7px 8px', border: '1px solid #e2e8f0', borderRadius: 6, fontSize: 13, resize: 'vertical', boxSizing: 'border-box', marginBottom: 14 }} />

            <div style={{ display: 'flex', gap: 8 }}>
              <button onClick={() => setEditingEquipment(null)} style={{ flex: 1, padding: 9, backgroundColor: '#edf2f7', border: '1px solid #e2e8f0', borderRadius: 6, cursor: 'pointer', fontSize: 13 }}>Cancel</button>
              <button onClick={saveEquipmentEdit} style={{ flex: 2, padding: 9, backgroundColor: '#3182ce', color: 'white', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 13, fontWeight: 'bold' }}>Save</button>
            </div>
          </div>
        </div>
      )}

      {/* Add / Edit power feed modal */}
      {editingFeed && (
        <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 3000, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: 16, overflowY: 'auto' }}>
          <div style={{ backgroundColor: 'white', borderRadius: 12, padding: 24, width: '100%', maxWidth: 420, boxShadow: '0 8px 32px rgba(0,0,0,0.3)', marginTop: 20 }}>
            <div style={{ fontWeight: 'bold', fontSize: 16, marginBottom: 16 }}>{editingFeed.id ? 'Edit Power Feed' : 'Add Power Feed'}</div>
            <InlineField label="Feed Name *" value={editingFeed.feed_name} onChange={v => setEditingFeed(p => ({ ...p, feed_name: v }))} />
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              <InlineField label="Cable #" value={editingFeed.cable_number} onChange={v => setEditingFeed(p => ({ ...p, cable_number: v }))} />
              <InlineField label="Breaker #" value={editingFeed.breaker_number} onChange={v => setEditingFeed(p => ({ ...p, breaker_number: v }))} />
            </div>
            <InlineField label="Cabinet / Panel Name" value={editingFeed.cabinet_name} onChange={v => setEditingFeed(p => ({ ...p, cabinet_name: v }))} />
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              <InlineField label="Feed Side" value={editingFeed.feed_side} onChange={v => setEditingFeed(p => ({ ...p, feed_side: v }))}
                options={[['left','Left'],['right','Right'],['other','Other']]} />
              <InlineField label="Voltage" value={String(editingFeed.voltage_vdc || '125')} onChange={v => setEditingFeed(p => ({ ...p, voltage_vdc: v }))}
                options={[['125','125 VDC'],['48','48 VDC']]} />
            </div>
            <InlineField label="Feed Type" value={editingFeed.feed_type} onChange={v => setEditingFeed(p => ({ ...p, feed_type: v }))}
              options={[['dc_substation','DC Substation'],['ac_pdu','AC PDU'],['other','Other']]} />
            {!editingFeed.id && (
              <div>
                <InlineField label="Number of Fuse Blocks" type="number" value={editingFeed.fuse_count} onChange={v => setEditingFeed(p => ({ ...p, fuse_count: v }))} />
                {editingFeed.fuse_count > 0 && (
                  <div style={{ fontSize: 11, color: '#718096', marginTop: -6, marginBottom: 10, backgroundColor: '#f7fafc', padding: '6px 8px', borderRadius: 5, border: '1px solid #e2e8f0' }}>
                    Will create: <strong>1{editingFeed.feed_side === 'right' ? 'R' : 'L'}</strong> (reserved — incoming feed)
                    {parseInt(editingFeed.fuse_count) > 1 && <> + <strong>2{editingFeed.feed_side === 'right' ? 'R' : 'L'} – {editingFeed.fuse_count}{editingFeed.feed_side === 'right' ? 'R' : 'L'}</strong> (available for equipment)</>}
                  </div>
                )}
              </div>
            )}
            <div style={{ marginBottom: 14 }}>
              <label style={{ fontSize: 12, color: '#718096', display: 'block', marginBottom: 3 }}>Notes</label>
              <textarea value={editingFeed.notes || ''} onChange={e => setEditingFeed(p => ({ ...p, notes: e.target.value }))} rows={2}
                style={{ width: '100%', padding: '7px 8px', border: '1px solid #e2e8f0', borderRadius: 6, fontSize: 13, resize: 'vertical', boxSizing: 'border-box' }} />
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button onClick={() => setEditingFeed(null)} style={{ flex: 1, padding: 9, backgroundColor: '#edf2f7', border: '1px solid #e2e8f0', borderRadius: 6, cursor: 'pointer', fontSize: 13 }}>Cancel</button>
              <button onClick={saveFeed} disabled={!editingFeed.feed_name?.trim()}
                style={{ flex: 2, padding: 9, backgroundColor: editingFeed.feed_name?.trim() ? '#3182ce' : '#a0aec0', color: 'white', border: 'none', borderRadius: 6, cursor: editingFeed.feed_name?.trim() ? 'pointer' : 'not-allowed', fontSize: 13, fontWeight: 'bold' }}>
                {editingFeed.id ? 'Save Changes' : 'Add Feed'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add equipment modal */}
      {addingAt && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 3000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ backgroundColor: 'white', borderRadius: 12, padding: 24, width: 320, boxShadow: '0 8px 32px rgba(0,0,0,0.3)' }}>
            <div style={{ fontWeight: 'bold', fontSize: 16, marginBottom: 16 }}>Add {EQUIPMENT_TYPES.find(t => t.key === addingAt.type)?.label}</div>
            <div style={{ fontSize: 13, color: '#718096', marginBottom: 12 }}>Unit {addingAt.unit} · {addingAt.height}U</div>
            <input autoFocus type="text" placeholder="Label (e.g. Panel A)" value={newLabel} onChange={e => setNewLabel(e.target.value)} onKeyDown={e => e.key === 'Enter' && confirmAdd()}
              style={{ width: '100%', padding: '8px 10px', border: '1px solid #ccc', borderRadius: 6, fontSize: 13, boxSizing: 'border-box', marginBottom: 16 }} />
            <div style={{ display: 'flex', gap: 8 }}>
              <button onClick={() => setAddingAt(null)} style={{ flex: 1, padding: 9, backgroundColor: '#edf2f7', border: '1px solid #e2e8f0', borderRadius: 6, cursor: 'pointer', fontSize: 13 }}>Cancel</button>
              <button onClick={confirmAdd} disabled={!newLabel.trim()} style={{ flex: 1, padding: 9, backgroundColor: newLabel.trim() ? '#3182ce' : '#a0aec0', color: 'white', border: 'none', borderRadius: 6, cursor: newLabel.trim() ? 'pointer' : 'not-allowed', fontSize: 13, fontWeight: 'bold' }}>Add</button>
            </div>
          </div>
        </div>
      )}

      <FMPPreviewModal
        fmp={fmpPreview}
        onClose={() => setFmpPreview(null)}
        onSubmit={submitStandaloneFmp}
        submitting={submittingFmp}
      />
    </div>
  );
}
